import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  Color,
  Deck,
  LeagueSettings,
  Match,
  MatchEntry,
  Pilot,
  PowerLevel,
  WinCondition,
} from '@edh/shared';
import {
  BASE,
  applyMatch,
  deckLabel,
  resolveMatch,
  revertMatch,
  seedElo,
  skillOf,
  winChances,
  type RatingSnapshot,
} from '@edh/shared';
import { OfflineError, fetchState, send, setLeader as apiSetLeader } from './api';
import { cacheGuildIdentity } from './brand';
import { subject } from './cognito';
import {
  dequeue,
  emptyState,
  enqueue,
  pending,
  readState,
  writeState,
  type LeagueState,
  type Mutation,
} from './db';

/**
 * The client applies every mutation to its own copy first and queues the request
 * behind it. The rating engine is the same module the Lambda runs, so the number
 * that animates on the table is the number the server will agree on — the sync is
 * a confirmation, not a correction.
 */

export interface MatchResult {
  match: Match;
  revealed: { pilots: string[]; decks: string[] };
}

interface StoreValue {
  state: LeagueState;
  /** The signed-in account's own member record, once it exists. */
  me: Pilot | undefined;
  /** Guild leaders can remove members and grant the role. Anyone can invite. */
  isLeader: boolean;
  setLeader: (pilotId: string, leader: boolean) => Promise<void>;
  /** The local cache has been read. Says nothing about whether it is current. */
  loading: boolean;
  /**
   * The server has answered at least once this session, or has definitively
   * failed to. Anything that decides something from an absence — "you are not a
   * member yet, so register" — has to wait for this, or a device with a cold
   * cache asks a member to register in the half second before the league
   * arrives.
   */
  hydrated: boolean;
  online: boolean;
  queued: number;
  error: string | null;
  /** Set when a match has just resolved, so the bars can animate. Cleared by the UI. */
  lastResult: MatchResult | null;
  clearResult: () => void;

  createPilot: (name: string) => Promise<Pilot>;
  updatePilot: (id: string, patch: Partial<Pilot>) => Promise<void>;
  createDeck: (input: NewDeckInput) => Promise<Deck>;
  updateDeck: (id: string, patch: Partial<Deck>) => Promise<void>;
  deletePilot: (id: string) => Promise<void>;
  deleteDeck: (id: string) => Promise<void>;
  logMatch: (input: LogMatchInput) => Promise<MatchResult>;
  /** Leaders only. Unwinds back to the game and replays everything after it. */
  deleteMatch: (matchId: string) => Promise<void>;
  undoLast: () => Promise<Match | null>;
  editMatch: (patch: {
    matchId: string;
    winnerPilotId?: string;
    endTurn?: number | null;
    winCondition?: WinCondition | null;
    note?: string | null;
  }) => Promise<void>;
  setSettings: (patch: Partial<LeagueSettings>) => Promise<void>;
  sync: () => Promise<void>;
}

export interface NewDeckInput {
  name: string;
  /** What the table calls it. Falls back to the commander. */
  nickname?: string | null;
  colors?: Color[];
  ownerId?: string | null;
  scryfallId?: string | null;
  artCropUrl?: string | null;
  /** The second commander, for a partner pair. */
  partner?: Deck['partner'];
  /** A data URI, for a deck with no card to fetch art from. */
  customArt?: string | null;
  powerLevel?: PowerLevel | null;
}

export interface LogMatchInput {
  entries: MatchEntry[];
  /** Null for a cancelled game, which nobody won. */
  winnerPilotId: string | null;
  /** The table played and it did not resolve. Logged, shown, worth nothing. */
  cancelled?: boolean;
  /**
   * When it was played. Defaults to now.
   *
   * Set when re-logging a corrected game, so it stays on the night it happened
   * rather than moving to the night somebody noticed the mistake.
   */
  ts?: number;
  /** Skips the victory screen. For a correction, which is not a result. */
  silent?: boolean;
  sessionId?: string | null;
  endTurn?: number;
  onThePlay?: string;
  mulligans?: Record<string, number>;
  winCondition?: WinCondition;
  mode?: 'standard' | 'draft';
  lifeRemaining?: Record<string, number>;
  /** Finishing order, from the counter. Only live games have one. */
  standing?: Record<string, number>;
  note?: string;
  /** Odds at kick-off. The server recomputes them if this is missing. */
  predicted?: Record<string, number>;
  /** Only present for games played through the live counter. */
  damageLog?: { to: string; from: string | null; delta: number; turn: number; step?: number }[];
  poison?: Record<string, number>;
  poisonLog?: { to: string; from: string | null; delta: number; turn: number; step?: number }[];
  commanderDamage?: Record<string, Record<string, number>>;
  durationSeconds?: number;
  /** Who knocked whom out, as the table confirmed it. Pays the killer in a pod. */
  takedowns?: Record<string, string>;
  /** Every turn the game had, so the life chart reads a turn at a time. */
  turnLog?: { seat: number; turn: number }[];
}

const StoreContext = createContext<StoreValue | null>(null);

const uuid = () =>
  crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

function snapshotOf(state: LeagueState): RatingSnapshot {
  const snap: RatingSnapshot = { pilots: {}, decks: {} };
  for (const p of state.pilots) {
    // Must match the server's snapshot in every field it reads, or the
    // optimistic deltas differ from the ones that come back.
    snap.pilots[p.id] = {
      elo: p.elo,
      progress: p.progress ?? 0,
      placementGames: p.placementGames,
    };
  }
  for (const d of state.decks) {
    snap.decks[d.id] = {
      elo: d.elo,
      peakElo: d.peakElo,
      placementGames: d.placementGames,
      // Must match the server's snapshot, or the optimistic deltas would differ
      // from the ones that come back.
      powerLevel: d.powerLevel,
    };
  }
  return snap;
}

function writeBack(state: LeagueState, snap: RatingSnapshot): LeagueState {
  return {
    ...state,
    pilots: state.pilots.map((p) => (snap.pilots[p.id] ? { ...p, ...snap.pilots[p.id]! } : p)),
    decks: state.decks.map((d) => (snap.decks[d.id] ? { ...d, ...snap.decks[d.id]! } : d)),
  };
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LeagueState>(emptyState);
  const [loading, setLoading] = useState(true);
  const [hydrated, setHydrated] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<MatchResult | null>(null);
  const syncing = useRef(false);

  /**
   * Actions read state through this ref rather than the render closure. Two taps
   * inside a single render pass would otherwise both build their next state from
   * the same stale snapshot and the second would silently discard the first —
   * which on game night looks like the deck you just added vanishing.
   */
  const stateRef = useRef(state);

  // Persist every state change so a hard close mid-evening loses nothing.
  const commit = useCallback((next: LeagueState) => {
    stateRef.current = next;
    setState(next);
    void writeState(next);
    // The sign-in screen renders before any of this is available, so the guild's
    // identity is kept somewhere it can reach without a token.
    cacheGuildIdentity(next.settings.guildName, next.settings.guildIcon);
    return next;
  }, []);

  const refreshQueueCount = useCallback(async () => {
    setQueued((await pending()).length);
  }, []);

  /**
   * Drain the queue in order, then take the server's state as authoritative.
   * Never overwrite local state while writes are still pending — the server has
   * not seen them yet and would hand back a version of the evening that is
   * missing the last two games.
   */
  const sync = useCallback(async () => {
    if (syncing.current) return;
    syncing.current = true;
    try {
      for (const item of await pending()) {
        try {
          await send(item.mutation);
          await dequeue(item.seq!);
        } catch (err) {
          if (err instanceof OfflineError) return; // try again on reconnect
          // A rejected mutation would block the queue forever. Drop it, keep the
          // rest moving, and let the refetch below correct the local state.
          console.error('mutation rejected, dropping', item.mutation, err);
          await dequeue(item.seq!);
          setError(err instanceof Error ? err.message : 'a change was rejected');
        }
      }
      await refreshQueueCount();

      const server = await fetchState();
      commit(server);
      setHydrated(true);
      setError(null);
    } catch (err) {
      if (!(err instanceof OfflineError)) {
        setError(err instanceof Error ? err.message : 'sync failed');
      }
      // Offline is an answer too. Blocking the app behind a spinner that can
      // never resolve is worse than working from whatever the cache holds.
      setHydrated(true);
    } finally {
      syncing.current = false;
      await refreshQueueCount();
    }
  }, [commit, refreshQueueCount]);

  // Boot: local cache first so the app is usable before the network answers.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const cached = await readState();
      if (!cancelled && cached) commit(cached);
      if (!cancelled) setLoading(false);
      await refreshQueueCount();
      await sync();
    })();
    return () => {
      cancelled = true;
    };
  }, [sync, refreshQueueCount, commit]);

  useEffect(() => {
    const up = () => {
      setOnline(true);
      void sync();
    };
    const down = () => setOnline(false);
    /*
     * Coming back to the app is a reason to re-read the league.
     *
     * Installed to a home screen there is no address bar to reload from and no
     * browser pull-to-refresh, so an app that only synced at boot could sit on
     * a day-old league all evening and look like it was broken. Bringing it to
     * the front is the closest thing to asking for fresh data.
     */
    const wake = () => {
      if (document.visibilityState === 'visible') void sync();
    };
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    document.addEventListener('visibilitychange', wake);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
      document.removeEventListener('visibilitychange', wake);
    };
  }, [sync]);

  const queueAndSync = useCallback(
    async (mutation: Mutation) => {
      await enqueue(mutation);
      await refreshQueueCount();
      void sync();
    },
    [sync, refreshQueueCount],
  );

  // ---- Actions --------------------------------------------------------------

  const createPilot = useCallback(
    async (name: string) => {
      const pilot: Pilot = {
        id: uuid(),
        name: name.trim(),
        elo: BASE,
        placementGames: 0,
        retired: false,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        // Added by hand, so nobody has signed in as them yet. They can claim
        // this record later and keep whatever rating it has earned.
        sub: null,
        avatar: null,
        // The role is granted by a leader, never inherited from being added.
        leader: false,
        // Guests have no account, so there is no address to show.
        email: null,
      };
      const current = stateRef.current;
      commit({ ...current, pilots: [...current.pilots, pilot] });
      await queueAndSync({ kind: 'createPilot', payload: { id: pilot.id, name: pilot.name } });
      return pilot;
    },
    [commit, queueAndSync],
  );

  const updatePilot = useCallback(
    async (id: string, patch: Partial<Pilot>) => {
      const current = stateRef.current;
      commit({
        ...current,
        pilots: current.pilots.map((p) => (p.id === id ? { ...p, ...patch } : p)),
      });
      await queueAndSync({ kind: 'updatePilot', payload: { id, ...patch } });
    },
    [commit, queueAndSync],
  );

  const createDeck = useCallback(
    async (input: NewDeckInput) => {
      const deck: Deck = {
        id: uuid(),
        name: input.name.trim(),
        nickname: input.nickname?.trim() || null,
        colors: input.colors ?? [],
        ownerId: input.ownerId ?? null,
        scryfallId: input.scryfallId ?? null,
        artCropUrl: input.artCropUrl ?? null,
        partner: input.partner ?? null,
        customArt: input.customArt ?? null,
        artCached: false,
        // Same seed the server will apply, so the optimistic row does not jump
        // when the sync lands.
        elo: seedElo(input.powerLevel),
        peakElo: seedElo(input.powerLevel),
        placementGames: 0,
        retired: false,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        lastRebuiltAt: null,
        powerLevel: input.powerLevel ?? null,
      };
      const current = stateRef.current;
      commit({ ...current, decks: [...current.decks, deck] });
      await queueAndSync({
        kind: 'createDeck',
        payload: {
          id: deck.id,
          name: deck.name,
          colors: deck.colors,
          ownerId: deck.ownerId,
          scryfallId: deck.scryfallId,
          artCropUrl: deck.artCropUrl,
          partner: deck.partner,
          powerLevel: deck.powerLevel,
        },
      });
      return deck;
    },
    [commit, queueAndSync],
  );

  const updateDeck = useCallback(
    async (id: string, patch: Partial<Deck>) => {
      const current = stateRef.current;
      commit({
        ...current,
        decks: current.decks.map((d) => (d.id === id ? { ...d, ...patch } : d)),
      });
      await queueAndSync({ kind: 'updateDeck', payload: { id, ...patch } });
    },
    [commit, queueAndSync],
  );

  /**
   * Removal, not retirement — for a row that should never have existed. The
   * server refuses if anything has games on record, so the guard here is only
   * about not showing the option in the first place.
   */
  const deletePilot = useCallback(
    async (id: string) => {
      const current = stateRef.current;
      commit({ ...current, pilots: current.pilots.filter((p) => p.id !== id) });
      await queueAndSync({ kind: 'deletePilot', payload: { id } });
    },
    [commit, queueAndSync],
  );

  const deleteDeck = useCallback(
    async (id: string) => {
      const current = stateRef.current;
      commit({ ...current, decks: current.decks.filter((d) => d.id !== id) });
      await queueAndSync({ kind: 'deleteDeck', payload: { id } });
    },
    [commit, queueAndSync],
  );

  const logMatch = useCallback(
    async (input: LogMatchInput) => {
      const current = stateRef.current;
      const snap = snapshotOf(current);

      /*
       * The odds, settled before the engine runs because the upset bonus is
       * paid off them — and computed here exactly the way the server does it,
       * or the optimistic result would disagree with the one that comes back.
       *
       * A game logged after the fact has no kick-off to freeze, so the ratings
       * as they stand now are the best available answer.
       */
      const predicted =
        input.predicted ??
        Object.fromEntries(
          input.entries.map((e, i) => [
            e.pilotId,
            winChances(
              input.entries.map((x) => {
                const p = current.pilots.find((y) => y.id === x.pilotId)!;
                const d = current.decks.find((y) => y.id === x.deckId)!;
                // Skill, matching the server exactly — see Contender.
                return {
                  pilotElo: skillOf(p),
                  deckElo: d.elo,
                  deckGames: d.placementGames,
                  powerLevel: d.powerLevel,
                };
              }),
            )[i]!,
          ]),
        );
      /*
       * Only odds the table was actually shown pay an upset bonus.
       *
       * A game typed in afterwards has odds reconstructed from today's ratings
       * for a table that sat down last week — worth recording, since the
       * calibration chart wants something, and not worth paying anybody for,
       * because nobody was ever shown it. Mirrors resolvePrediction on the
       * server, or the optimistic result would disagree.
       */
      const frozen = input.predicted !== undefined;

      const cancelled = input.cancelled === true;
      /*
       * Nothing was won, so nothing is owed - and the same is true on the
       * server, which is the only reason the optimistic row and the one that
       * comes back agree about a cancelled game.
       */
      const { deltas, revealed } = cancelled
        ? { deltas: { pilots: {}, decks: {} }, revealed: { pilots: [], decks: [] } }
        : resolveMatch(
            {
              entries: input.entries,
              winnerPilotId: input.winnerPilotId!,
              lifeRemaining: input.lifeRemaining,
              // Same inputs as the server, or the optimistic row disagrees with
              // the one that comes back.
              endTurn: input.endTurn,
              standing: input.standing,
              takedowns: input.takedowns,
              predicted: frozen ? predicted : undefined,
            },
            snap,
            // Fixed rules, matching the server — see RATING_RULES in the API.
            // They were settings once; a switch that changes what a game is
            // worth halfway through a season leaves a ladder measuring two
            // things.
            { marginOfVictory: true, progressBonus: true },
          );

      const clientId = uuid();
      const match: Match = {
        id: clientId,
        ts: input.ts ?? Date.now(),
        entries: input.entries,
        winnerPilotId: cancelled ? null : input.winnerPilotId,
        cancelled: cancelled ? true : undefined,
        sessionId: input.sessionId ?? null,
        endTurn: input.endTurn,
        onThePlay: input.onThePlay,
        mulligans: input.mulligans,
        winCondition: input.winCondition,
        lifeRemaining: input.lifeRemaining,
        // Measurements survive a cancellation; results do not. See logMatch on
        // the server, which drops the same two fields for the same reason.
        standing: cancelled ? undefined : input.standing,
        note: input.note?.trim() || undefined,
        // The same figures the upset bonus above was paid from.
        predicted,
        damageLog: input.damageLog,
        poison: input.poison,
        poisonLog: input.poisonLog,
        commanderDamage: input.commanderDamage,
        durationSeconds: input.durationSeconds,
        takedowns: cancelled ? undefined : input.takedowns,
        turnLog: input.turnLog,
        deltas,
        revealed,
        clientId,
      };

      applyMatch(snap, deltas);
      commit({ ...writeBack(current, snap), matches: [...current.matches, match] });

      const result = { match, revealed };
      if (!input.silent && !cancelled) setLastResult(result);
      await queueAndSync({ kind: 'logMatch', payload: { ...match, clientId } });
      return result;
    },
    [commit, queueAndSync],
  );

  const undoLast = useCallback(async () => {
    const current = stateRef.current;
    const last = [...current.matches].sort((a, b) => a.ts - b.ts).at(-1);
    if (!last) return null;

    const snap = snapshotOf(current);
    revertMatch(snap, last.deltas);
    // peakElo is not invertible from a delta; the server recomputes it from the
    // surviving history and the next sync brings the real number back.
    commit({
      ...writeBack(current, snap),
      matches: current.matches.filter((m) => m.id !== last.id),
    });
    setLastResult(null);
    await queueAndSync({ kind: 'undoLast', payload: {} });
    return last;
  }, [commit, queueAndSync]);

  /**
   * Correct a game that is not the most recent one.
   *
   * No optimistic write. Every rating after the edited game is recomputed, and
   * doing that twice — once here on a guess and once on the server for real —
   * would put two different answers on the screen a second apart. The sync that
   * follows brings back the version that counts.
   */
  const editMatch = useCallback(
    async (patch: {
      matchId: string;
      winnerPilotId?: string;
      endTurn?: number | null;
      winCondition?: WinCondition | null;
      note?: string | null;
    }) => {
      await queueAndSync({ kind: 'editMatch', payload: patch });
      await sync();
    },
    [queueAndSync, sync],
  );

  /**
   * Remove a game from the league entirely.
   *
   * No optimistic write, for the same reason as `editMatch`: the server unwinds
   * back to this game and replays everything after it, and guessing that here
   * would put a different set of ratings on the screen for the second before
   * the sync corrects them.
   */
  const deleteMatch = useCallback(
    async (matchId: string) => {
      await queueAndSync({ kind: 'deleteMatch', payload: { matchId } });
      await sync();
    },
    [queueAndSync, sync],
  );

  const setSettings = useCallback(
    async (patch: Partial<LeagueSettings>) => {
      const current = stateRef.current;
      commit({ ...current, settings: { ...current.settings, ...patch } });
      await queueAndSync({ kind: 'settings', payload: patch });
    },
    [commit, queueAndSync],
  );

  const clearResult = useCallback(() => setLastResult(null), []);

  const mySub = subject();
  const me = mySub ? state.pilots.find((p) => p.sub === mySub) : undefined;

  /**
   * Not queued offline. A role change is an administrative act whose whole
   * point is that other people can see it, and the server has to agree — the
   * UI only ever mirrors a decision it does not get to make.
   */
  const setLeaderRole = useCallback(
    async (pilotId: string, leader: boolean) => {
      await apiSetLeader(pilotId, leader);
      await sync();
    },
    [sync],
  );

  const value = useMemo<StoreValue>(
    () => ({
      state,
      me,
      isLeader: Boolean(me?.leader),
      setLeader: setLeaderRole,
      loading,
      hydrated,
      online,
      queued,
      error,
      lastResult,
      clearResult,
      createPilot,
      updatePilot,
      createDeck,
      updateDeck,
      deletePilot,
      deleteDeck,
      logMatch,
      deleteMatch,
      undoLast,
      editMatch,
      setSettings,
      sync,
    }),
    [
      state, me, setLeaderRole, loading, hydrated, online, queued, error, lastResult, clearResult,
      createPilot, updatePilot, createDeck, updateDeck, deletePilot, deleteDeck,
      logMatch, undoLast, deleteMatch, editMatch, setSettings, sync,
    ],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): StoreValue {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore outside StoreProvider');
  return ctx;
}

/** Lookup helpers used all over the UI. */
export function useLookups() {
  const { state } = useStore();
  return useMemo(
    () => ({
      pilot: (id: string) => state.pilots.find((p) => p.id === id),
      deck: (id: string) => state.decks.find((d) => d.id === id),
      pilotName: (id: string) => state.pilots.find((p) => p.id === id)?.name ?? 'Unknown',
      // The nickname if there is one — this is the name the app speaks in.
      deckName: (id: string) => {
        const d = state.decks.find((x) => x.id === id);
        return d ? deckLabel(d) : 'Unknown deck';
      },
    }),
    [state.pilots, state.decks],
  );
}
